<template>
  <Listbox
    as="div"
    :class="{
      'relative grow': iconName || slots.icon || selectorStyle === 'input',
      'opacity-60 !cursor-not-allowed select-none': disabled,
    }"
    v-model="selectedValues"
    @update:model-value="onInput"
    :multiple="multiple"
    :disabled="disabled"
  >
    <ListboxButton
      v-if="selectorStyle === 'input'"
      ref="dropdownSelectorButton"
      class="relative flex w-full cursor-pointer rounded-sm border border-gray-300 bg-white py-2 pl-3 pr-10 text-left shadow-sm focus:border-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-500 sm:text-sm"
      :class="{
        '!cursor-not-allowed': disabled,
      }"
    >
      <span class="grow flex items-center">
        {{ getSelectedValueLabel(selectedValues) }}
      </span>
      <span
        class="absolute inset-y-0 right-0 flex items-center pr-2 text-gray-400"
      >
        <ChevronDownIcon
          v-if="amountOfValuesSelected === 0"
          class="h-5 w-5"
          aria-hidden="true"
        />
        <XMarkIcon
          v-else
          @click.prevent="clear"
          class="h-5 w-5"
          aria-hidden="true"
        />
      </span>
      <div
        class="absolute inset-y-0 right-7 mx-2 flex items-center"
        v-if="hint"
      >
        <Tooltip :content="hint" origin="right">
          <HelpCircle class="w-4 h-4 text-gray-400" />
        </Tooltip>
      </div>
    </ListboxButton>

    <template v-else>
      <ListboxButton
        v-if="!iconName && !slots.icon"
        ref="dropdownSelectorButton"
        class="whitespace-nowrap flex items-center rounded-sm border-transparent hover:bg-sky-100 hover:text-sky-800 transition py-2 px-3 sm:text-sm"
        :class="{
          'bg-sky-100 text-sky-800': amountOfValuesSelected > 0,
          'bg-gray-100 text-gray-900': amountOfValuesSelected < 1,
          'pointer-events-none': disabled,
        }"
      >
        <span class="flex items-center">
          {{
            amountOfValuesSelected === 1
              ? getSelectedValueLabel(selectedValues)
              : label
          }}
          <span
            v-if="amountOfValuesSelected > 1"
            class="ml-1 inline-flex items-center rounded-full bg-sky-200 px-2 py-0.5 text-xs font-medium text-sky-800"
          >
            {{ amountOfValuesSelected }}
          </span>
        </span>
        <span class="ml-2">
          <ChevronDownIcon
            v-if="amountOfValuesSelected === 0"
            class="h-5 w-5 text-gray-900"
            aria-hidden="true"
          />
          <XMarkIcon
            v-else
            @click.prevent="clear"
            class="h-5 w-5 text-sky-800"
            aria-hidden="true"
          />
        </span>
      </ListboxButton>

      <ListboxButton
        v-else
        class="flex items-center transition text-sm text-gray-500 font-medium hover:text-gray-900 group"
        :class="{
          'pointer-events-none': disabled,
        }"
        ref="dropdownSelectorButton"
      >
        <span>
          <span class="flex items-center space-x-2" :class="iconColor">
            <slot v-if="slots.icon" name="icon" />
            <span v-else class="material-symbols-outlined">
              {{ iconName }}
            </span>
            <span class="text-gray-500 group-hover:text-gray-900">{{
              getSelectedValueLabel(selectedValues)
            }}</span>
          </span>
        </span>
      </ListboxButton>
    </template>

    <transition
      leave-active-class="transition ease-in duration-100"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <ListboxOptions
        :class="[
          iconName || slots.icon ? `left-[-${horizontalLabelWidth}]` : '',
          {
            'mt-0 mb-3': shouldRenderOnTop,
            'max-w-auto': selectorStyle === 'input',
          },
        ]"
        class="z-backdrop-reset mt-2 max-h-56 max-w-md min-w-fit flex flex-col rounded-sm bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none text-sm fixed"
        :style="placementStyles"
      >
        <FilterSearchInput
          v-model="searchQuery"
          :placeholder="t('components.inputs.search')"
        />

        <div class="overflow-auto" v-if="filteredOptions.length > 0">
          <template v-for="option in filteredOptions">
            <ListboxOption
              as="template"
              v-if="option.show ? option.show() : true"
              :key="option.key ?? option.label"
              :value="option.value"
              v-slot="{ active, selected }"
            >
              <li
                :class="[
                  active ? 'text-gray-900 bg-gray-100' : 'text-gray-900',
                  'relative cursor-pointer select-none py-2 pl-3 pr-9',
                ]"
              >
                <div class="flex items-center space-x-2">
                  <input
                    class="h-4 w-4 rounded-xs border-gray-300 text-brand-900 focus:ring-0 focus:outline-0 focus :ring-offset-0"
                    type="checkbox"
                    :checked="selected"
                  />
                  <span
                    :class="[
                      'truncate font-normal',
                      selected && 'font-semibold',
                    ]"
                  >
                    {{ option.label }}
                  </span>
                </div>
              </li>
            </ListboxOption>
          </template>
        </div>

        <ListboxOption
          disabled
          v-else
          class="px-3 py-2 text-gray-500 whitespace-nowrap"
        >
          {{ t("components.no_result") }}
        </ListboxOption>
      </ListboxOptions>
    </transition>
  </Listbox>
</template>

<script setup>
import {
  computed,
  defineEmits,
  defineProps,
  onBeforeMount,
  ref,
  useSlots,
  watch,
  onMounted,
} from "vue";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/vue";
import { XMarkIcon, ChevronDownIcon } from "@heroicons/vue/20/solid";
import { useI18n } from "vue-i18n";
import FilterSearchInput from "@/Tailwind/Components/Input/Filters/FilterSearchInput";
import { useReactiveRendering } from "@/Tailwind/Mixins/useReactiveRendering";
import HelpCircle from "@/Tailwind/Icon/HelpCircle.vue";
import Tooltip from "@/Tailwind/Components/Tooltip.vue";

const { t } = useI18n();

const emit = defineEmits(["change"]);

const props = defineProps({
  value: [Array, String],
  default: [Array, String],
  // Since we are not using typescript, each option should include following properties: label, key (optional), value
  options: Array,
  label: String,
  multiple: {
    type: Boolean,
    default: false,
  },
  iconName: {
    type: String,
  },
  iconColor: {
    type: Object, // Object of styles
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  horizontalLabelWidth: {
    type: String,
    default: "0rem",
  },
  selectorStyle: {
    type: String,
  },
  emptyLabel: {
    type: String,
  },
  hint: {
    type: String,
  },
  preFilled: {
    type: Boolean,
    default: false,
  },
});

const selectedValues = ref();
const slots = useSlots();

// Search
const searchQuery = ref("");

const filteredOptions = computed(() => {
  if (!searchQuery.value) {
    return props.options;
  }

  return props.options?.filter((option) =>
    option.label.toLowerCase().includes(searchQuery.value.toLowerCase())
  );
});

onBeforeMount(() => {
  setSelectedValues(props.value);
});

watch(
  () => props.value,
  (value) => {
    setSelectedValues(value);
  }
);

watch(
  () => [props.options, props.preFilled],
  () => {
    if (props.options?.length === 1 && props.preFilled) {
      emit("change", props.options[0].value);
    }
  }
);

onMounted(() => {
  if (props.options?.length === 1 && props.preFilled) {
    emit("change", props.options[0].value);
  }
});

const setSelectedValues = (newValue) => {
  if (!newValue) {
    selectedValues.value = props.multiple ? [] : null;

    return;
  }

  const selectedOptions = props.options
    .filter((option) => {
      if (Array.isArray(option.value)) {
        return option.value.some((value) =>
          Array.isArray(newValue)
            ? newValue.includes(value)
            : newValue === value
        );
      }

      return Array.isArray(newValue)
        ? newValue.includes(option.value)
        : newValue === option.value;
    })
    // SelectedValues hold the ids
    .map((option) => option.value);

  selectedValues.value = props.multiple ? selectedOptions : selectedOptions[0];
};

const getSelectedValueLabel = (value) => {
  if (props.selectorStyle === "input") {
    const options = props.options.filter((option) => {
      if (Array.isArray(option.value)) {
        return option.value.some((val) =>
          Array.isArray(value) ? value.includes(val) : value === val
        );
      }

      return Array.isArray(value)
        ? value.includes(option.value)
        : value === option.value;
    });

    return options.length === 0
      ? props.emptyLabel ?? t("components.buttons.make_selection")
      : options.map((option) => option.label).join(", ");
  }

  if (Array.isArray(value)) {
    value = value[0];
  }

  const option = props.options.find((option) => {
    if (Array.isArray(option.value)) {
      return option.value.some((val) =>
        Array.isArray(value) ? value.includes(val) : value === val
      );
    }

    return Array.isArray(value)
      ? value.includes(option.value)
      : value === option.value;
  });

  return option
    ? option.label
    : props.emptyLabel ?? t("components.inputs.expansion.no_value");
};

const amountOfValuesSelected = computed(() => {
  // When multiple mode is not enabled, selectedValues will be one single item
  if (Array.isArray(selectedValues.value)) {
    return selectedValues.value.length;
  }

  return selectedValues.value ? 1 : 0;
});

const onInput = (value) => {
  let valueToEmit = value === props.value ? null : value;

  if (props.default && !valueToEmit) {
    valueToEmit = props.default;
  }

  emit("change", valueToEmit);
};

const clear = () => {
  const value = props.multiple ? [] : null;

  emit("change", value);
  selectedValues.value = value;
};

const dropdownSelectorButton = ref(null);
const minimumRequiredSpace = computed(() => {
  // Each list item element is 36px tall
  // The container is max 224px tall
  // Search input is 50px tall
  // Leave 70px at the bottom as a margin (Some screens have floating elements)
  return Math.min(filteredOptions.value.length * 36 + 50, 224) + 70;
});

const { shouldRenderOnTop, placementStyles } = useReactiveRendering(
  dropdownSelectorButton,
  minimumRequiredSpace,
  props.scrollContainer
);
</script>

<style>
.material-symbols-outlined {
  font-variation-settings: "FILL" 0, "wght" 200, "GRAD" -25, "opsz" 20;
}
</style>
